import { Fragment } from 'react';
import { Placeholder, RegaraNoteList, TitledSection, withInlinePlaceholders } from './DossierPrimitives';

/**
 * GSPR (MDR Annex I) conformity checklist table renderer.
 *
 * Mirrors _render_gspr() in backend/services/dossier/eu_combined_renderer/__init__.py.
 * Columns: § | Requirement | Applicable | Method of conformity | Standard(s)/CS | Evidence ref
 *
 * Placeholder discipline:
 *   - Cells equal to "[Submitter must complete]" surface the shared Placeholder chip.
 *   - Cells carrying inline __PLACEHOLDER__:hint markers surface via withInlinePlaceholders.
 *   - null / undefined cells surface as a Placeholder chip.
 *
 * Data shape (GsprMatrix from app/dossier/eu/documents/gspr_checklist.py):
 *   { device_class, rows: GsprMatrixRow[], notes: string[] }
 *   GsprMatrixRow: { section, chapter, requirement, applicable, method_of_conformity,
 *                    standards: string[], evidence_reference, addressed }
 */

const SUBMITTER_MARKER = '[Submitter must complete]';
const PLACEHOLDER_TOKEN = '__PLACEHOLDER__';

function gsprCell(value: unknown): React.ReactNode {
  if (value == null || value === '') {
    return <Placeholder hint={null} />;
  }
  const s = String(value);
  if (s === SUBMITTER_MARKER || s.startsWith(PLACEHOLDER_TOKEN)) {
    if (s.startsWith(`${PLACEHOLDER_TOKEN}:`)) {
      return <Placeholder hint={s.slice(PLACEHOLDER_TOKEN.length + 1).trim() || null} />;
    }
    if (s.trim() === PLACEHOLDER_TOKEN) {
      return <Placeholder hint={null} />;
    }
    // literal "[Submitter must complete]"
    return <Placeholder hint={null} />;
  }
  return withInlinePlaceholders(s);
}

type GsprRow = {
  section?: string | null;
  chapter?: string | null;
  requirement?: string | null;
  applicable?: string | null;
  method_of_conformity?: string | null;
  standards?: string[] | null;
  evidence_reference?: string | null;
};

export function GsprMatrixRenderer({ matrix }: { matrix: Record<string, unknown> }) {
  const rows = Array.isArray(matrix.rows) ? (matrix.rows as GsprRow[]) : [];
  const notes = Array.isArray(matrix.notes) ? (matrix.notes as string[]) : [];

  // Group rows by chapter — emit a chapter-head row whenever the chapter changes.
  let lastChapter: string | null | undefined = undefined;

  return (
    <TitledSection title="GSPR Conformity Checklist (Annex I)">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12.5px]" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} />
            <col style={{ width: '27%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '23%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <thead>
            <tr>
              {['§', 'Requirement', 'Applicable', 'Method of conformity', 'Standard(s)/CS', 'Evidence ref'].map(
                (h) => (
                  <th
                    key={h}
                    className="text-left p-2 bg-navy text-white text-[11px] font-semibold border-b border-line"
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="p-2 border-b border-line text-muted italic text-[13px]"
                >
                  No GSPR rows — run the TD agent to populate the checklist.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const ch = row.chapter;
                const chapterChanged = ch !== lastChapter;
                if (chapterChanged) lastChapter = ch;

                return (
                  <Fragment key={`row-${idx}`}>
                    {chapterChanged && (
                      <tr
                        key={`ch-${idx}`}
                        className="bg-[#eef1f6]"
                      >
                        <td
                          colSpan={6}
                          className="p-2 border-b border-line font-bold text-navy text-[12px]"
                          style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >
                          Chapter {ch ?? '—'}
                        </td>
                      </tr>
                    )}
                    <tr key={`row-${idx}`} className="hover:bg-[#fafafa]">
                      <td
                        className="p-2 border-b border-line align-top"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {row.section ?? '—'}
                      </td>
                      <td
                        className="p-2 border-b border-line align-top"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {row.requirement ? withInlinePlaceholders(row.requirement) : <Placeholder hint={null} />}
                      </td>
                      <td
                        className="p-2 border-b border-line align-top"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {gsprCell(row.applicable)}
                      </td>
                      <td
                        className="p-2 border-b border-line align-top"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {gsprCell(row.method_of_conformity)}
                      </td>
                      <td
                        className="p-2 border-b border-line align-top"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {row.standards && row.standards.length > 0 ? (
                          row.standards.map((s, si) => (
                            <span key={si}>
                              {si > 0 && ', '}
                              {withInlinePlaceholders(s)}
                            </span>
                          ))
                        ) : (
                          gsprCell(null)
                        )}
                      </td>
                      <td
                        className="p-2 border-b border-line align-top"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {gsprCell(row.evidence_reference)}
                      </td>
                    </tr>
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <RegaraNoteList notes={notes} />
    </TitledSection>
  );
}
